import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/reduxHooks';
import { useMercureOrders } from '@/hooks/useMercureOrders';
import { usePlaySound } from '@/hooks/usePlaySound';
import { useNativeOrderNotification } from '@/hooks/useNativeOrderNotification';
import { addLiveOrder } from '@/store/orders';
import { enqueueOrderNotification } from '@/store/notifications';
import { markUnviewed } from '@/store/unviewedOrders';
import { type ISocketOrderEvent } from '@/types/ISocketOrderEvent';

/**
 * Глобальный слушатель событий заказов из Mercure. Живёт на уровне App
 * (вне Routes), чтобы соединение было одно на сессию и не рвалось при
 * навигации. Подписка активна только после проверки пользователя и при
 * наличии токена. Сам «тупой»: вся логика — в хуках, утилитах и сторе.
 */
export const MercureOrdersListener: React.FC = () => {
  const dispatch = useAppDispatch();
  const isUserChecked = useAppSelector((state) => state.auth.isUserChecked);
  // tokensReady — реактивный признак записанных токенов (вместо нереактивного
  // AuthService.isAuthorized), чтобы подписка стартовала сразу после логина.
  const tokensReady = useAppSelector((state) => state.auth.tokensReady);
  const enabled = isUserChecked && tokensReady;

  const { play } = usePlaySound();
  const notifyNative = useNativeOrderNotification();

  const handleOrderEvent = useCallback(
    (event: ISocketOrderEvent) => {
      dispatch(enqueueOrderNotification(event));
      dispatch(markUnviewed(event.data.uuid));
      play();

      // Нативный ОС-баннер — только когда вкладка не видна (в фокусе свой тост);
      // вся условная логика (права/тумблер/видимость) внутри хука.
      notifyNative(event);

      // Эфемерный realtime-слой поверх таблицы: новый заказ виден сверху на
      // любой странице/фильтрах, сбрасывается при следующем запросе списка.
      dispatch(addLiveOrder(event.data));
    },
    [dispatch, play, notifyNative]
  );

  useMercureOrders({ onOrderEvent: handleOrderEvent, enabled });

  return null;
};
